/**
 * Editing a photo's colours in the browser (1.177.1): the CSS chain of a
 * filter with its strength and sliders, the matrices behind it, and the
 * colour grading after Lightroom's, down to the loop over a picture's
 * pixels.
 *
 * Built for the collage and taken out of it on purpose. From use: "die
 * Funktionen so isoliert bauen, dass sie später auch in ‚normale' Fotos
 * eingebaut werden können." Nothing here knows a collage: a photo of its own
 * takes the same `PhotoAdjust`, the same `photoFilter()` for CSS and the
 * same `gradePixels()` where CSS is not enough (`lib/editedPicture.ts`).
 *
 * **The server has the twin** (`PhotoEdit`), held to the same cases
 * (`tools/pruefstand/gradefaelle.json`, `grade.ts`). Whoever changes a rule
 * here changes it there.
 */

/** The four regions of a colour grading, as Lightroom names them (1.177.0). */
export const GRADE_REGIONS = ['shadows', 'midtones', 'highlights', 'global'] as const
export type GradeRegion = (typeof GRADE_REGIONS)[number]

/** Hue 0–359, saturation 0–100, luminance −100..100. */
export interface GradeValues {
  hue: number
  saturation: number
  luminance: number
}

/** A colour grading (1.177.0), as the server checks it (`PhotoEdit::grade()`). */
export type PhotoGrade = Record<GradeRegion, GradeValues> & { blend: number; balance: number }

/** A grading to start from: nothing moves until a saturation or a luminance does. */
export function emptyGrade(): PhotoGrade {
  return {
    shadows: { hue: 0, saturation: 0, luminance: 0 },
    midtones: { hue: 0, saturation: 0, luminance: 0 },
    highlights: { hue: 0, saturation: 0, luminance: 0 },
    global: { hue: 0, saturation: 0, luminance: 0 },
    blend: 50,
    balance: 0,
  }
}

/** Null for a grading that changes nothing, as the server stores it. */
export function activeGrade(grade: PhotoGrade | null | undefined): PhotoGrade | null {
  if (grade == null) return null
  return GRADE_REGIONS.some((r) => grade[r].saturation !== 0 || grade[r].luminance !== 0) ? grade : null
}

/** One CSS filter function of a filter, as the server lists it (`PhotoEdit::FILTERS`). */
export interface FilterStep {
  fn: 'brightness' | 'contrast' | 'saturate' | 'sepia' | 'grayscale' | 'hue-rotate'
  value: number
}

/** The values of an edit: filter, strength, sliders and grading (1.176.0, 1.177.0); a collage's and an own filter's alike. */
export interface PhotoAdjust {
  filter: string
  strength: number
  brightness: number
  contrast: number
  saturation: number
  grade: PhotoGrade | null
}

export const NEUTRAL_ADJUST: PhotoAdjust = { filter: 'none', strength: 100, brightness: 100, contrast: 100, saturation: 100, grade: null }

/**
 * A filter as a setting of the controls (1.178.0). From use: "wenn ein
 * Filter ausgewählt wird, sollen sich die Regler entsprechend anpassen" --
 * so a filter is no longer a CSS chain of its own (`PhotoEdit::FILTERS`,
 * which stays for the collages that carry a name) but brightness, contrast,
 * saturation and a colour grading, and choosing one puts the controls
 * there. The looks follow the chains they replace: sepia became a warm
 * global tint, a hue rotation the tint's hue. Tuned on the device.
 */
export interface Preset {
  brightness?: number
  contrast?: number
  saturation?: number
  grade?: Partial<Record<GradeRegion, { hue: number; saturation: number; luminance?: number }>>
}

export const PRESETS: Record<string, Preset> = {
  none: {},
  golden: { brightness: 102, saturation: 130, grade: { global: { hue: 35, saturation: 45 } } },
  lemon: { brightness: 108, contrast: 112, saturation: 125, grade: { global: { hue: 55, saturation: 40 } } },
  mist: { brightness: 105, contrast: 90, saturation: 40, grade: { global: { hue: 340, saturation: 20 } } },
  rose: { saturation: 65, grade: { global: { hue: 345, saturation: 30 } } },
  blossom: { saturation: 130, grade: { global: { hue: 330, saturation: 40 } } },
  summer: { brightness: 110, contrast: 115, saturation: 120, grade: { highlights: { hue: 40, saturation: 35 } } },
  everyday: { saturation: 45, grade: { global: { hue: 90, saturation: 20 } } },
  glacier: { brightness: 125, contrast: 90, saturation: 35, grade: { global: { hue: 200, saturation: 30 } } },
  ocean: { saturation: 130, grade: { global: { hue: 210, saturation: 50 } } },
  plum: { brightness: 88, saturation: 75, grade: { global: { hue: 290, saturation: 30 } } },
  lavender: { brightness: 92, contrast: 70, saturation: 85, grade: { global: { hue: 265, saturation: 35 } } },
  amber: { brightness: 89, contrast: 80, grade: { global: { hue: 30, saturation: 20 } } },
  mono: { saturation: 0, contrast: 110 },
}

/**
 * The values a preset puts on the controls, pulled towards neutral by its
 * strength (100 is the preset as listed, 0 is nothing). A name without a
 * preset is neutral.
 */
export function presetAdjust(key: string, strength = 100): PhotoAdjust {
  const preset = PRESETS[key]
  const share = Math.max(0, Math.min(100, strength)) / 100
  if (preset === undefined) return { ...NEUTRAL_ADJUST, strength }
  const towards = (value: number | undefined): number => Math.round(100 + ((value ?? 100) - 100) * share)
  const grade = emptyGrade()
  for (const region of GRADE_REGIONS) {
    const g = preset.grade?.[region]
    if (g !== undefined) grade[region] = { hue: g.hue, saturation: Math.round(g.saturation * share), luminance: Math.round((g.luminance ?? 0) * share) }
  }
  return {
    filter: 'none',
    strength,
    brightness: towards(preset.brightness),
    contrast: towards(preset.contrast),
    saturation: towards(preset.saturation),
    grade: activeGrade(grade),
  }
}

const NEUTRAL: Record<FilterStep['fn'], number> = { brightness: 1, contrast: 1, saturate: 1, sepia: 0, grayscale: 0, 'hue-rotate': 0 }

/**
 * The CSS `filter` of a collage's photos (1.176.0): its filter pulled
 * towards neutral by its strength, then the three sliders -- the same chain
 * as `PhotoEdit::chain()`, which the reader's picture draws per pixel. What
 * changes nothing is left out, and nothing at all is `none`.
 */
export function photoFilter(adjust: PhotoAdjust, filters: Record<string, FilterStep[]> | undefined): string {
  const parts = filterSteps(adjust, filters).map(([fn, v]) => (fn === 'hue-rotate' ? `hue-rotate(${round(v)}deg)` : `${fn}(${round(v)})`))
  return parts.length === 0 ? 'none' : parts.join(' ')
}

/** The chain of `photoFilter()` as numbers: `PhotoEdit::chain()` in the browser. */
export function filterSteps(adjust: PhotoAdjust, filters: Record<string, FilterStep[]> | undefined): [FilterStep['fn'], number][] {
  const share = adjust.strength / 100
  const steps: [FilterStep['fn'], number][] = (filters?.[adjust.filter] ?? []).map((s) => [s.fn, NEUTRAL[s.fn] + (s.value - NEUTRAL[s.fn]) * share])
  steps.push(['brightness', adjust.brightness / 100], ['contrast', adjust.contrast / 100], ['saturate', adjust.saturation / 100])
  return steps.filter(([fn, v]) => Math.abs(v - NEUTRAL[fn]) > 1e-9)
}

/**
 * One filter function as a 3 × 4 matrix (1.177.0): the equivalents of the
 * Filter Effects specification, as `PhotoEdit::matrix()` has them. The
 * browser needs them only where it draws a graded photo itself
 * (`lib/editedPicture.ts`); everywhere else CSS draws the chain.
 */
/** Rows r, g, b, the fourth column the offset. */
export type Matrix = [number, number, number, number, number, number, number, number, number, number, number, number]

export function filterMatrix([fn, v]: [FilterStep['fn'], number]): Matrix {
  switch (fn) {
    case 'brightness':
      return [v, 0, 0, 0, 0, v, 0, 0, 0, 0, v, 0]
    case 'contrast': {
      const c = 0.5 - 0.5 * v
      return [v, 0, 0, c, 0, v, 0, c, 0, 0, v, c]
    }
    case 'saturate':
      return [
        0.213 + 0.787 * v, 0.715 - 0.715 * v, 0.072 - 0.072 * v, 0,
        0.213 - 0.213 * v, 0.715 + 0.285 * v, 0.072 - 0.072 * v, 0,
        0.213 - 0.213 * v, 0.715 - 0.715 * v, 0.072 + 0.928 * v, 0,
      ]
    case 'grayscale': {
      const a = 1 - Math.min(1, v)
      return [
        0.2126 + 0.7874 * a, 0.7152 - 0.7152 * a, 0.0722 - 0.0722 * a, 0,
        0.2126 - 0.2126 * a, 0.7152 + 0.2848 * a, 0.0722 - 0.0722 * a, 0,
        0.2126 - 0.2126 * a, 0.7152 - 0.7152 * a, 0.0722 + 0.9278 * a, 0,
      ]
    }
    case 'sepia': {
      const a = 1 - Math.min(1, v)
      return [
        0.393 + 0.607 * a, 0.769 - 0.769 * a, 0.189 - 0.189 * a, 0,
        0.349 - 0.349 * a, 0.686 + 0.314 * a, 0.168 - 0.168 * a, 0,
        0.272 - 0.272 * a, 0.534 - 0.534 * a, 0.131 + 0.869 * a, 0,
      ]
    }
    case 'hue-rotate': {
      const cos = Math.cos((v * Math.PI) / 180)
      const sin = Math.sin((v * Math.PI) / 180)
      return [
        0.213 + cos * 0.787 - sin * 0.213, 0.715 - cos * 0.715 - sin * 0.715, 0.072 - cos * 0.072 + sin * 0.928, 0,
        0.213 - cos * 0.213 + sin * 0.143, 0.715 + cos * 0.285 + sin * 0.14, 0.072 - cos * 0.072 - sin * 0.283, 0,
        0.213 - cos * 0.213 - sin * 0.787, 0.715 - cos * 0.715 + sin * 0.715, 0.072 + cos * 0.928 + sin * 0.072, 0,
      ]
    }
  }
}

/** How far a saturation of 100 moves a colour, and a luminance of 100 the light (as `PhotoEdit::GRADE_*_GAIN`). */
const GRADE_SATURATION_GAIN = 0.3
const GRADE_LUMINANCE_GAIN = 0.3

export interface GradeTerms {
  add: Record<GradeRegion, [number, number, number]>
  pivot: number
  width: number
}

/**
 * What a grading adds, worked out once per picture (1.177.0). **The same
 * rule as `PhotoEdit::gradeTerms()`**, and held to the same cases
 * (`tools/pruefstand/gradefaelle.json`): per region the hue's colour at
 * full saturation and half light, less its own luma, times the saturation,
 * plus the luminance on all three channels; the pivot moves with the
 * balance, the width of the blends with the blending.
 */
export function gradeTerms(grade: PhotoGrade): GradeTerms {
  const add = {} as Record<GradeRegion, [number, number, number]>
  for (const region of GRADE_REGIONS) {
    const { hue, saturation, luminance } = grade[region]
    const [cr, cg, cb] = hueColour(hue)
    const luma = 0.2126 * cr + 0.7152 * cg + 0.0722 * cb
    const tint = (saturation / 100) * GRADE_SATURATION_GAIN
    const light = (luminance / 100) * GRADE_LUMINANCE_GAIN
    add[region] = [(cr - luma) * tint + light, (cg - luma) * tint + light, (cb - luma) * tint + light]
  }
  return { add, pivot: 0.5 - (0.25 * grade.balance) / 100, width: 0.25 + (0.5 * grade.blend) / 100 }
}

/** One colour graded, sRGB 0..1 (`PhotoEdit::gradeApply()`). */
export function gradeApply(r: number, g: number, b: number, terms: GradeTerms): [number, number, number] {
  const light = 0.2126 * r + 0.7152 * g + 0.0722 * b
  const p = terms.pivot
  const t = light <= p ? (0.5 * light) / p : 0.5 + (0.5 * (light - p)) / (1 - p)
  const s = terms.width
  const weights: Record<GradeRegion, number> = {
    shadows: smooth(1 - t / s),
    midtones: smooth(1 - Math.abs(t - 0.5) / s),
    highlights: smooth(1 - (1 - t) / s),
    global: 1,
  }
  let [or, og, ob] = [r, g, b]
  for (const region of GRADE_REGIONS) {
    const w = weights[region]
    const [ar, ag, ab] = terms.add[region]
    or += w * ar
    og += w * ag
    ob += w * ab
  }
  return [clamp01(or), clamp01(og), clamp01(ob)]
}

/** `gradeTerms()` and `gradeApply()` in one, for a single colour. */
export function gradeColour(r: number, g: number, b: number, grade: PhotoGrade): [number, number, number] {
  return gradeApply(r, g, b, gradeTerms(grade))
}

/** A hue at full saturation and half light, r, g, b in 0..1. */
function hueColour(hue: number): [number, number, number] {
  const h = hue / 60
  const x = 1 - Math.abs((h % 2) - 1)
  if (h < 1) return [1, x, 0]
  if (h < 2) return [x, 1, 0]
  if (h < 3) return [0, 1, x]
  if (h < 4) return [0, x, 1]
  if (h < 5) return [x, 0, 1]
  return [1, 0, x]
}

/** The colour a hue stands for in the sheet (its dot): full saturation, half light. */
export function hueCss(hue: number): string {
  const [r, g, b] = hueColour(hue)
  return `rgb(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)})`
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

function smooth(x: number): number {
  const c = x < 0 ? 0 : x > 1 ? 1 : x
  return c * c * (3 - 2 * c)
}

/**
 * The chain and the grading over a picture's pixels, in place (1.177.0):
 * the drawing of `PhotoEdit::filterPixels()`, the same order, a step
 * clamped after each, the same colour computed once.
 */
export function gradePixels(data: Uint8ClampedArray, steps: [FilterStep['fn'], number][], grade: PhotoGrade | null): void {
  const matrices = steps.map(filterMatrix)
  // Without a grading the chain alone (1.178.0): the table of
  // `lib/editedPicture.ts` holds both, and a preset's thumbnail has no grading.
  const terms = grade === null ? null : gradeTerms(grade)
  const memo = new Map<number, number>()
  for (let i = 0; i < data.length; i += 4) {
    const key = (data[i]! << 16) | (data[i + 1]! << 8) | data[i + 2]!
    let out = memo.get(key)
    if (out === undefined) {
      let r = data[i]! / 255
      let g = data[i + 1]! / 255
      let b = data[i + 2]! / 255
      for (const m of matrices) {
        const nr = m[0] * r + m[1] * g + m[2] * b + m[3]
        const ng = m[4] * r + m[5] * g + m[6] * b + m[7]
        const nb = m[8] * r + m[9] * g + m[10] * b + m[11]
        r = nr < 0 ? 0 : nr > 1 ? 1 : nr
        g = ng < 0 ? 0 : ng > 1 ? 1 : ng
        b = nb < 0 ? 0 : nb > 1 ? 1 : nb
      }
      if (terms !== null) [r, g, b] = gradeApply(r, g, b, terms)
      out = (Math.round(r * 255) << 16) | (Math.round(g * 255) << 8) | Math.round(b * 255)
      if (memo.size > 200_000) memo.clear()
      memo.set(key, out)
    }
    data[i] = (out >> 16) & 0xff
    data[i + 1] = (out >> 8) & 0xff
    data[i + 2] = out & 0xff
  }
}

function round(v: number): number {
  return Math.round(v * 10000) / 10000
}
